import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type ListPromotionsResult,
  type ListRuleSchemasResult,
} from '@whinerp/client';
import {
  EmptyState,
  ErrorState,
  Num,
  PageHeading,
  Panel,
  RuleBuilder,
  SectionLabel,
  Skeleton,
  cx,
} from '@whinerp/ui';
import type { RuleNode, RuleSchema } from '@whinerp/contracts/rules';
import { formatCount, formatDate, formatMoney, formatRelativeDate } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { ruleBuilderLabels } from './rule-labels.js';

/**
 * SC-47 Promotions and loyalty.
 *
 * A coupon is a promotion with a code somebody types; an automatic promotion is
 * one without. The same rows, the same conditions, the same engine - so a rule
 * that works on one works on the other, and a workspace cannot end up with two
 * promotion engines that disagree about what "order total over 500" means.
 *
 * **Four states, not one grey row.** Expired is reissued; used up has its limit
 * raised; switched off is switched on; not yet started is waited for. A list
 * that showed all four the same way would hide the difference between four
 * actions.
 *
 * **The usage count has a log behind it.** "How many times has this been used"
 * is asked in an argument about an invoice, and a number with nothing behind it
 * cannot answer it.
 */

type Promotions = ListPromotionsResult;
type Promotion = Promotions['promotions'][number];
type Tab = 'all' | 'coupons' | 'automatic';

function PromotionsScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const [tab, setTab] = useState<Tab>('all');
  const [selected, setSelected] = useState<string | null>(null);

  const promotions = useQuery<Promotions, ApiError>({
    queryKey: ['promotions'],
    queryFn: () => api.listPromotions({}),
  });

  const schemas = useQuery<ListRuleSchemasResult, ApiError>({
    queryKey: ['rule-schemas'],
    queryFn: () => api.listRuleSchemas({}),
  });

  const schema = useMemo<RuleSchema | null>(() => {
    const found = schemas.data?.schemas.find((entry) => entry.key === 'promotion');
    return (found as RuleSchema | undefined) ?? null;
  }, [schemas.data]);

  if (promotions.isError) {
    return <PromotionsError error={promotions.error} retry={() => void promotions.refetch()} />;
  }
  if (!promotions.data || !schema) return <PromotionsLoading />;

  const all = promotions.data.promotions;
  const shown = all.filter((promotion) =>
    tab === 'all' ? true : tab === 'coupons' ? promotion.code !== null : promotion.code === null,
  );
  const chosen = all.find((promotion) => promotion.promotionId === selected) ?? null;

  if (all.length === 0) return <PromotionsEmpty reason="no-records" />;

  return (
    <div data-component="Promotions" className="flex w-full flex-col gap-4">
      <header className="flex flex-wrap items-baseline gap-3">
        <PageHeading>{t('promotions.title')}</PageHeading>
        {/* Type tabs. A coupon and an automatic promotion are the same row
            with and without a code, and the tabs say so rather than implying
            two different things. */}
        <div role="tablist" aria-label={t('promotions.title')} className="ms-auto flex gap-1">
          {(['all', 'coupons', 'automatic'] as const).map((option) => (
            <button
              key={option}
              type="button"
              role="tab"
              data-tab={option}
              aria-selected={tab === option}
              onClick={() => setTab(option)}
              className={cx(
                'h-btn rounded-control px-btn-x text-dense',
                tab === option ? 'bg-bronze text-bronze-on' : 'text-ink-600 hover:bg-sunken',
              )}
            >
              {t(
                option === 'all'
                  ? 'promotions.all'
                  : option === 'coupons'
                    ? 'promotions.coupons'
                    : 'promotions.automatic',
              )}
            </button>
          ))}
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[45%_55%]">
        <Panel data-region="list">
          <ul className="flex flex-col">
            {shown.map((promotion) => (
              <li key={promotion.promotionId}>
                <button
                  type="button"
                  data-promotion={promotion.promotionId}
                  aria-pressed={selected === promotion.promotionId}
                  onClick={() => setSelected(promotion.promotionId)}
                  className={cx(
                    'flex w-full flex-col gap-1 border-t border-t-rule p-2 text-start first:border-t-0',
                    selected === promotion.promotionId ? 'bg-sunken' : 'hover:bg-sunken',
                  )}
                >
                  <span className="flex flex-wrap items-baseline gap-2">
                    <span className="text-body text-ink">{promotion.name}</span>
                    {promotion.code ? (
                      <Num data-promotion-code className="text-meta text-ink-meta">
                        {promotion.code}
                      </Num>
                    ) : (
                      <span className="text-meta text-ink-meta">{t('promotions.noCode')}</span>
                    )}
                    <StateChip promotion={promotion} />
                  </span>
                  <span className="flex flex-wrap items-baseline gap-3 text-meta text-ink-meta">
                    <Num data-promotion-usage={promotion.promotionId}>
                      {promotion.maxRedemptions === null
                        ? t('promotions.used', {
                            values: { count: formatCount(promotion.redemptions, format) },
                          })
                        : t('promotions.usedOf', {
                            values: {
                              count: formatCount(promotion.redemptions, format),
                              max: formatCount(promotion.maxRedemptions, format),
                            },
                          })}
                    </Num>
                    {promotion.validTo ? (
                      <Num data-promotion-expiry={promotion.promotionId}>
                        {t('promotions.until', {
                          values: { date: formatDate(`${promotion.validTo}T12:00:00Z`, format) },
                        })}
                      </Num>
                    ) : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Panel>

        <div className="flex flex-col gap-4">
          {chosen === null ? (
            <Panel data-region="detail">
              <p className="text-body text-ink-meta">{t('promotions.select')}</p>
            </Panel>
          ) : (
            <>
              <Panel data-region="conditions">
                <div className="flex flex-col gap-3">
                  <SectionLabel>{t('promotions.conditions')}</SectionLabel>
                  {/* The one builder, given the promotion schema. Read-only
                      here: editing a live promotion's conditions changes what
                      a coupon somebody is holding does, and that needs its own
                      confirmation rather than an autosave. */}
                  <RuleBuilder
                    schema={schema}
                    value={chosen.conditions as RuleNode}
                    onChange={() => undefined}
                    labels={ruleBuilderLabels(t)}
                    matchCount={null}
                    readOnly
                  />
                </div>
              </Panel>
              <RedemptionLog promotion={chosen} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Why a promotion would not apply, in words.
 *
 * Four states rather than a grey row, because each is a different action:
 * expired is reissued, used up has its limit raised, switched off is switched
 * on, and not yet started is simply waited for.
 */
function StateChip({ promotion }: { promotion: Promotion }): JSX.Element {
  const t = useT();
  const state = promotion.unavailable;
  return (
    <span
      data-promotion-state={state ?? 'live'}
      className={cx(
        'rounded-chip px-1.5 text-meta',
        state === null
          ? 'bg-success-tint text-success-text'
          : state === 'exhausted' || state === 'expired'
            ? 'bg-warning-tint text-warning-text'
            : 'bg-sunken text-ink-600',
      )}
    >
      {state === null ? t('promotions.state.live') : t(`promotions.state.${state}` as 'promotions.state.expired')}
    </span>
  );
}

/** What the usage count is made of. A number with nothing behind it settles no argument. */
function RedemptionLog({ promotion }: { promotion: Promotion }): JSX.Element {
  const t = useT();
  const format = useFormat();
  const log = useQuery({
    queryKey: ['promotion-redemptions', promotion.promotionId],
    queryFn: () => api.getPromotionRedemptions({ path: { promotionId: promotion.promotionId } }),
  });

  return (
    <Panel data-region="log">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('promotions.log')}</SectionLabel>
        {log.isPending ? (
          <Skeleton className="h-block-sm w-full" />
        ) : log.isError ? (
          <ErrorState
            title={t('state.error')}
            body={log.error.message}
            onRetry={() => void log.refetch()}
          />
        ) : log.data.redemptions.length === 0 ? (
          <p className="text-body text-ink-meta">{t('promotions.log.empty')}</p>
        ) : (
          <>
            <ul className="flex flex-col">
              {log.data.redemptions.map((entry) => (
                <li
                  key={entry.redemptionId}
                  data-redemption={entry.redemptionId}
                  className="flex items-baseline gap-3 border-t border-t-rule py-1 first:border-t-0"
                >
                  <span className="min-w-0 flex-1 truncate text-body text-ink">
                    {entry.redeemedBy ?? ''}
                  </span>
                  <Num className="shrink-0 text-meta text-ink-meta">
                    {formatRelativeDate(entry.redeemedAt, format)}
                  </Num>
                  <Num className="shrink-0 text-body text-ink">
                    {formatMoney(entry.discount, format, { currency: entry.currency })}
                  </Num>
                </li>
              ))}
            </ul>
            {/* The gap, said rather than left to be noticed. A workspace
                running for a year has a counter older than the entries it
                keeps, and a log shorter than its count looks like a bug. */}
            {log.data.redemptions.length < promotion.redemptions ? (
              <p data-log-gap className="text-meta text-ink-meta">
                {t('promotions.log.gap')}
              </p>
            ) : null}
          </>
        )}
      </div>
    </Panel>
  );
}

function PromotionsEmpty({
  reason,
}: {
  reason: 'no-records' | 'filtered-to-nothing' | 'permission-denied';
}): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason={reason}
      illustration="Ticket"
      title={t('promotions.empty.title')}
      body={t('promotions.empty.body')}
      primary={{ label: t('empty.action.newPromotion'), icon: 'Plus', href: '/promotions' }}
    />
  );
}

function PromotionsLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-4" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h1 w-block-xl" />
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[45%_55%]">
        <Skeleton className="h-block-xl w-full" />
        <Skeleton className="h-block-xl w-full" />
      </div>
    </div>
  );
}

function PromotionsError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="Promotions could not be loaded"
      body={error instanceof Error ? error.message : 'The list did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-47',
  route: '/promotions',
  surface: 'page',
  title: 'Promotions & loyalty',
  features: ['F-163'],
  entitlement: 'F-163',
  reachedBy: [{ kind: 'setup', section: 'pricing' }],
  /*
   * The builder belongs to the detail pane, which exists only once somebody
   * has chosen a promotion from the list. Declared as needing an interaction,
   * which is the stronger demand: a UI test has to drive the product to the
   * point where it appears.
   *
   * The pane is not filled by default on purpose. Selecting the first row for
   * somebody fetches a redemption log they did not ask for, and the prompt
   * that sits there instead says what to do next.
   */
  components: [],
  afterInteraction: ['RuleBuilder'],
  elements: ['Promotions'],
  states: {
    empty: PromotionsEmpty,
    loading: PromotionsLoading,
    error: PromotionsError,
  },
  component: PromotionsScreen,
});

export default screen;
